import {filterToolsInputGroups} from '../toolsInputGroups/toolsInputGroupHelper';
import {AmountUnitsConfig, PriceUnitsConfig} from './AmountUnits.config';
import {DefaultSingleValueConfig} from './DefaultSingleValueConfig.config';
import {
  AdvertisementType,
  InputGroupCode,
  MainAdvertisementType,
} from './unit.types';

/**
 * Safely determines the correct inputCode to use.
 * Falls back to DEFAULT (0) if the provided one doesn't exist in config.
 */
function getInputCode(
  groupConfig: Record<number, any>,
  inputCode: number,
): number {
  if (!groupConfig) return 0; // no group found → fallback to default

  // If the given inputCode exists, return it, otherwise fallback to 0
  return groupConfig.hasOwnProperty(inputCode) ? inputCode : 0;
}

/**
 * Returns amount-based units (physical quantities like gram, liter, ton, etc.)
 */
export function getAmountUnits(
  advertisementType: AdvertisementType,
  inputGroupCode: InputGroupCode,
  inputCode: number,
  subInputCode?: number,
) {
  const isToolsInputGroup = inputGroupCode === InputGroupCode.TOOL;
  if (isToolsInputGroup && subInputCode) {
    return filterToolsInputGroups({
      subInputCode,
      inputCode,
    });
  } else if (isToolsInputGroup && !subInputCode) {
    return [];
  }
  // Get the transaction type config (e.g., buy, providedService, etc.)
  const transactionConfig = AmountUnitsConfig[advertisementType];

  if (!transactionConfig) {
    return [];
  }

  // Get the group config for this transaction type
  const groupConfig = transactionConfig[inputGroupCode];

  if (!groupConfig) {
    return [];
  }

  // Get valid input code or fallback
  const validCode = getInputCode(groupConfig, inputCode);

  return groupConfig[validCode] || [];
}

/**
 * Returns price units (like hourly, daily, yearly, etc.)
 */
export function getPriceUnits(
  advertisementType: AdvertisementType,
  inputGroupCode: InputGroupCode,
  inputCode: number = 0,
) {
  // Get the transaction type config (e.g., providedService, providedRent, etc.)
  const transactionConfig = PriceUnitsConfig[advertisementType];

  if (!transactionConfig) {
    return [];
  }

  // Get the group config for this transaction type
  const groupConfig = transactionConfig[inputGroupCode];

  if (!groupConfig) {
    return [];
  }

  // Get valid input code or fallback
  const validCode = getInputCode(groupConfig, inputCode);

  const units = groupConfig[validCode];

  return units || [];
}

export function convertAdvertisementType(
  adType: MainAdvertisementType,
  adSubType?: AdvertisementType,
): AdvertisementType | null {
  const map: Record<
    MainAdvertisementType,
    AdvertisementType | Partial<Record<AdvertisementType, AdvertisementType>>
  > = {
    [MainAdvertisementType.SALE]: AdvertisementType.SALE,
    [MainAdvertisementType.BUY]: AdvertisementType.BUY,

    [MainAdvertisementType.SERVICE]: {
      [AdvertisementType.PROVIDED_SERVICE]: AdvertisementType.PROVIDED_SERVICE,
      [AdvertisementType.WANTED_SERVICE]: AdvertisementType.WANTED_SERVICE,
    },

    [MainAdvertisementType.RENT]: {
      [AdvertisementType.PROVIDED_RENT]: AdvertisementType.PROVIDED_RENT,
      [AdvertisementType.WANTED_RENT]: AdvertisementType.WANTED_RENT,
    },
  };

  const value = map[adType];

  if (!value) return null;

  // direct type (buy, sale...)
  if (typeof value === 'string') return value;

  // subtype lookup (safe type narrowing)
  if (adSubType && typeof value === 'object' && adSubType in value) {
    return value[adSubType as AdvertisementType] ?? null;
  }

  return null;
}

export function getDefaultSingleValue(
  inputGroupCode: InputGroupCode,
  inputCode: number = 0,
): string | null {
  const groupConfig = DefaultSingleValueConfig[inputGroupCode];

  if (!groupConfig) return null;

  const validCode = getInputCode(groupConfig, inputCode);

  return groupConfig[validCode] ?? null;
}
